import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getAuth } from "firebase/auth";
import { LuCircleCheck, LuLock, LuSparkles } from "react-icons/lu";
import useAuthStatus from "../hooks/useAuthStatus";
import PropertyRequestFields from "../components/propertyRequest/PropertyRequestFields";
import PropertyRequestSummary from "../components/propertyRequest/PropertyRequestSummary";
import ContactFields from "../components/propertyRequest/ContactFields";
import DetectedCriteria from "../components/propertyRequest/DetectedCriteria";
import PropertyRequestMatches from "../components/propertyRequest/PropertyRequestMatches";
import { formatDate } from "../components/propertyRequest/labels";
import {
  coherentCriteria, emptyCriteria, fromCriteria, manualCriteria, toCriteria, toPayload, updateSources, validateContact, validateCriteria,
} from "../utils/propertyRequestForm";
import { clearDraft, loadDraft, saveDraft } from "../utils/propertyRequestDraft";
import { propertyRequestApi } from "../Services/propertyRequestService";

export default function PropertyRequestNew() {
  const { t, i18n } = useTranslation();
  const { loggedIn, checkingStatus } = useAuthStatus();
  const [initial] = useState(() => loadDraft());
  const [text, setText] = useState(initial?.text || "");
  const [criteria, setCriteria] = useState(() => (initial ? coherentCriteria(initial.criteria) : emptyCriteria()));
  // Origin of each criterion: "manual" (entered by the user), "ai" (found by Lina AI)
  // or "auto" (found by the fallback text analysis while Lina AI is unavailable).
  const [sources, setSources] = useState(initial?.sources || {});
  const [unscored, setUnscored] = useState(initial?.unscored || []);
  const [step, setStep] = useState(initial?.step || "edit");
  const [restored, setRestored] = useState(Boolean(initial));
  const [errors, setErrors] = useState({});
  const [contact, setContact] = useState({ preference: "email", email: "", phone: "" });
  const [prefilled, setPrefilled] = useState(false);
  const [contactErrors, setContactErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [created, setCreated] = useState(null);
  const [analysis, setAnalysis] = useState({ busy: false, message: "", error: "" });
  const [matches, setMatches] = useState({ loading: false, error: "", result: null });
  const heading = useRef(null);
  const analyzing = useRef(false);
  // Rate limited with a known wait (Retry-After): the text stays editable, the analysis is not resent before the end.
  const [retryUntil, setRetryUntil] = useState(0);
  useEffect(() => {
    if (!retryUntil) return undefined;
    const timer = setTimeout(() => setRetryUntil(0), Math.max(0, retryUntil - Date.now()));
    return () => clearTimeout(timer);
  }, [retryUntil]);

  // The e-mail of the signed-in account prefills the private contact (memory only).
  useEffect(() => {
    const email = loggedIn ? getAuth().currentUser?.email : "";
    if (email) { setContact((c) => (c.email ? c : { ...c, email })); setPrefilled(true); }
  }, [loggedIn]);

  // Text, criteria, sources and step survive a sign-in in this tab; contact details never do.
  useEffect(() => {
    if (!created) saveDraft({ text, criteria, sources, unscored, step: step === "matches" ? "preview" : step });
  }, [text, criteria, sources, unscored, step, created]);

  const go = (next) => {
    setStep(next);
    setApiError("");
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    setTimeout(() => heading.current?.focus(), 0);
  };

  const restart = () => {
    clearDraft();
    setText(""); setCriteria(emptyCriteria()); setSources({}); setUnscored([]); setErrors({}); setRestored(false); setCreated(null);
    setAnalysis({ busy: false, message: "", error: "" }); setMatches({ loading: false, error: "", result: null });
    go("edit");
  };

  // Any edit of the criteria makes the changed fields "manual" (manual always wins over a later analysis).
  const changeCriteria = (next) => {
    setSources((current) => updateSources(criteria, next, current));
    setCriteria(next);
  };

  const removeCriterion = (field) => changeCriteria(coherentCriteria({ ...criteria, [field]: emptyCriteria()[field] }));

  const analyze = async () => {
    if (analyzing.current || retryUntil > Date.now()) return;
    if (!text.trim()) { setAnalysis({ busy: false, message: "", error: t("propertyRequest.analysis.needText") }); return; }
    analyzing.current = true;
    setAnalysis({ busy: true, message: "", error: "" });
    try {
      const result = await propertyRequestApi.analyze(text, manualCriteria(criteria, sources), i18n.language);
      const merged = fromCriteria(result.merged);
      // Keep only the sources of the fields still present once the criteria are made coherent.
      const fallback = result.ai === "unavailable";
      const kept = Object.fromEntries(Object.entries(result.sources || {}).filter(([field]) =>
        Array.isArray(merged[field]) ? merged[field].length > 0 : merged[field] !== "")
        .map(([field, origin]) => [field, fallback && origin === "ai" ? "auto" : origin]));
      setCriteria(merged);
      setSources(kept);
      setUnscored(result.unscored || []);
      setErrors({});
      const found = Object.values(kept).includes("ai");
      setAnalysis({ busy: false, error: "",
        message: fallback ? t("propertyRequest.analysis.unavailable") : found ? t("propertyRequest.analysis.done") : t("propertyRequest.analysis.nothing") });
    } catch (error) {
      setAnalysis({ busy: false, message: "", error: error.message });
      if (error.status === 429 && error.retryAfter) setRetryUntil(Date.now() + error.retryAfter * 1000);
    } finally {
      analyzing.current = false;
    }
  };

  const toPreview = (event) => {
    event.preventDefault();
    const found = validateCriteria(criteria, text);
    setErrors(found);
    if (Object.keys(found).length === 0) go("preview");
  };

  const showMatches = async () => {
    const found = validateCriteria(criteria, text);
    if (Object.keys(found).length > 0) { setErrors(found); go("edit"); return; }
    go("matches");
    setMatches({ loading: true, error: "", result: null });
    try {
      setMatches({ loading: false, error: "", result: await propertyRequestApi.matches(toCriteria(criteria)) });
    } catch (error) {
      setMatches({ loading: false, error: error.message, result: null });
    }
  };

  const publish = async () => {
    const found = validateContact(contact);
    setContactErrors(found);
    if (Object.keys(found).length > 0 || submitting) return;
    const criteriaErrors = validateCriteria(criteria, text);
    if (Object.keys(criteriaErrors).length > 0) { setErrors(criteriaErrors); go("edit"); return; }
    setSubmitting(true); setApiError("");
    try {
      const result = await propertyRequestApi.create(toPayload(criteria, text, contact));
      clearDraft();
      setCreated(result);
      go("done");
    } catch (error) {
      setApiError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const title = { preview: "propertyRequest.preview.title", done: "propertyRequest.done.title", matches: "propertyRequest.matches.title" }[step] || "propertyRequest.new.title";
  const subtitle = step === "preview" ? t("propertyRequest.preview.subtitle") : step === "edit" ? t("propertyRequest.new.subtitle") : null;

  const analysisFooter = (
    <div className="mt-3 space-y-2">
      <button type="button" onClick={analyze} disabled={analysis.busy || !text.trim() || retryUntil > 0} aria-busy={analysis.busy}
        className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-2xl border-2 border-green-700 bg-white px-4 text-base font-bold text-green-800 transition hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-500/30 disabled:cursor-not-allowed disabled:opacity-60">
        <LuSparkles aria-hidden="true" />
        {analysis.busy ? t("propertyRequest.analysis.analyzing") : t("propertyRequest.analysis.button")}
      </button>
      {analysis.error && <p role="alert" className="text-sm font-semibold text-red-700">{analysis.error}</p>}
      {analysis.message && <p role="status" className="text-sm text-slate-700" data-analysis-message>{analysis.message}</p>}
    </div>
  );

  return (
    <main className="lina-property-request mx-auto w-full max-w-3xl px-4 py-6 sm:px-6 sm:py-10">
      <h1 ref={heading} tabIndex={-1} className="text-3xl font-black text-slate-900 focus:outline-none sm:text-4xl">{t(title)}</h1>
      {subtitle && <p className="mt-2 text-base text-slate-600">{subtitle}</p>}

      {restored && step !== "done" && (
        <div role="status" className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900">
          <span>{t("propertyRequest.new.draftRestored")}</span>
          <button type="button" className="font-semibold text-green-800 underline" onClick={restart}>{t("propertyRequest.new.restart")}</button>
        </div>
      )}

      {step === "edit" && (
        <form className="mt-6" noValidate onSubmit={toPreview}>
          <PropertyRequestFields text={text} onTextChange={setText} criteria={criteria} onCriteriaChange={changeCriteria} errors={errors}
            textFooter={analysisFooter}
            beforeCriteria={<DetectedCriteria criteria={criteria} sources={sources} unscored={unscored} onRemove={removeCriterion} />} />
          {Object.keys(errors).length > 0 && <p role="alert" className="mt-4 text-sm font-semibold text-red-700">{t("propertyRequest.errors.fixBelow")}</p>}
          <button type="submit" className="lina-action-primary mt-6 h-12 w-full text-base">{t("propertyRequest.new.continue")}</button>
        </form>
      )}

      {step === "preview" && (
        <div className="mt-6 space-y-5">
          <PropertyRequestSummary criteria={criteria} text={text} contactPreference={loggedIn ? contact.preference : null} />
          {unscored.length > 0 && (
            <p className="rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-900" data-unscored>
              <span className="font-semibold">{unscored.map((u) => t(`propertyRequest.attribute.${u}`)).join(" · ")}</span> — {t("propertyRequest.analysis.unscored")}
            </p>
          )}
          <div className="flex flex-col gap-2 sm:flex-row">
            <button type="button" className="lina-action-primary h-12 text-base" onClick={showMatches}>{t("propertyRequest.matches.see")}</button>
            <button type="button" className="lina-action h-12 text-base" onClick={() => go("edit")}>{t("propertyRequest.preview.edit")}</button>
          </div>
          {checkingStatus ? null : loggedIn ? (
            <>
              <ContactFields contact={contact} onChange={setContact} errors={contactErrors} prefilledEmail={prefilled && contact.email === getAuth().currentUser?.email} />
              <p className="text-sm text-slate-600">{t("propertyRequest.preview.lifetime")}</p>
              {apiError && <p role="alert" className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-800">{apiError}</p>}
              <button type="button" className="lina-action-primary h-12 w-full text-base" disabled={submitting} onClick={publish}>
                {submitting ? t("propertyRequest.preview.publishing") : t("propertyRequest.preview.publish")}
              </button>
            </>
          ) : (
            <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
              <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900"><LuLock aria-hidden="true" className="text-green-700" />{t("propertyRequest.preview.signInTitle")}</h2>
              <p className="mt-2 text-sm text-slate-600">{t("propertyRequest.preview.signInText")}</p>
              <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                <Link to="/SignIn" className="lina-action-primary h-12 text-base">{t("propertyRequest.preview.signIn")}</Link>
                <Link to="/signup" className="lina-action h-12 text-base">{t("propertyRequest.preview.signUp")}</Link>
              </div>
            </section>
          )}
        </div>
      )}

      {step === "matches" && (
        <div className="mt-6">
          {matches.loading && <p role="status" className="text-slate-600">{t("propertyRequest.matches.loading")}</p>}
          {matches.error && (
            <div role="alert" className="space-y-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-800">
              <p>{matches.error}</p>
              <button type="button" className="lina-action" onClick={showMatches}>{t("propertyRequest.mine.retry")}</button>
            </div>
          )}
          {matches.result && <PropertyRequestMatches result={matches.result} onPublish={() => go("preview")} onEdit={() => go("edit")} />}
        </div>
      )}

      {step === "done" && created && (
        <section className="mt-6 rounded-3xl border border-green-200 bg-green-50 p-5 sm:p-6">
          <p className="flex items-center gap-2 text-base font-semibold text-green-900">
            <LuCircleCheck aria-hidden="true" size={22} />
            {t("propertyRequest.done.text", { date: formatDate(created.expiresAt, i18n.language) })}
          </p>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            <Link to="/my-property-requests" className="lina-action-primary h-11">{t("propertyRequest.done.mine")}</Link>
            <Link to={`/property-request/${created.id}`} className="lina-action h-11">{t("propertyRequest.done.view")}</Link>
            <button type="button" className="lina-action h-11" onClick={restart}>{t("propertyRequest.done.another")}</button>
          </div>
        </section>
      )}
    </main>
  );
}
